/**
 * 網路硬碟分享（#24）：
 *  - 收到的分享（inbox）：依分享者分組 =「分享來自 XX」虛擬資料夾；可複製到我的硬碟
 *  - 我分享的（mine）：可撤回
 * 路由 ee_drive_share。權限沿 ee_my_drive。
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePermission } from '../../../hooks/usePermission';
import { useMessage } from '../../../contexts/MessageContext';
import FunctionPageHeader from '../../../components/FunctionPageHeader';
import {
  listInbox, listMine, listOrgInbox, listSharedFolder, openPreview, copyToMyDrive,
  copyToTenantDrive, revokeShare, ShareInbox, ShareMine, SharedFolderChild,
} from './services/driveShareService';

const FUNC = 'ee_my_drive';
const TENANT_DRIVE_FUNC = 'ee_tenant_drive';   // 「收進租戶硬碟」write action（role=3）
const GiB = 1024 ** 3;

function fmtSize(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 ** 2) return `${(b / 1024).toFixed(1)} KB`;
  if (b < GiB) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${(b / GiB).toFixed(2)} GB`;
}

const SharedWithMePage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError } = useMessage();

  const canRead = hasPermission(FUNC, 'read');
  const canCreate = hasPermission(FUNC, 'create');
  const canDelete = hasPermission(FUNC, 'delete');
  const canCopyToTenant = hasPermission(TENANT_DRIVE_FUNC, 'update');   // role=3 才顯「收進租戶硬碟」

  const [tab, setTab] = useState<'inbox' | 'mine' | 'org'>('inbox');
  const [inbox, setInbox] = useState<ShareInbox[]>([]);
  const [mine, setMine] = useState<ShareMine[]>([]);
  const [orgInbox, setOrgInbox] = useState<ShareInbox[]>([]);
  const [expanded, setExpanded] = useState<Record<number, SharedFolderChild[] | undefined>>({});
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [i, m, o] = await Promise.all([listInbox(), listMine(), listOrgInbox()]);
      setInbox(i); setMine(m); setOrgInbox(o); setExpanded({});
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { if (!permLoading && canRead) load(); }, [permLoading, canRead, load]);

  const doCopy = async (s: ShareInbox) => {
    setBusy(s.id);
    try {
      const r = await copyToMyDrive(s.id);
      showSuccess('SYS020001', { name: r.filename });   // {name}新增成功（複製進我的硬碟）
    } catch (err) { showApiError(err); }
    finally { setBusy(null); }
  };

  const doPreview = (shareId: number, childId?: number) =>
    openPreview(shareId, childId).catch(showApiError);

  const doExpand = async (s: ShareInbox) => {
    if (expanded[s.id]) { setExpanded((p) => ({ ...p, [s.id]: undefined })); return; }
    try {
      const children = await listSharedFolder(s.id);
      setExpanded((p) => ({ ...p, [s.id]: children }));
    } catch (err) { showApiError(err); }
  };

  const doCopyToTenant = async (s: ShareInbox) => {
    setBusy(s.id);
    try {
      await copyToTenantDrive(s.id);
      showSuccess('SYS020001', { name: s.filename });   // 已收進租戶硬碟
    } catch (err) { showApiError(err); }
    finally { setBusy(null); }
  };

  const doRevoke = async (s: ShareMine) => {
    if (!window.confirm(t('driveShare.revoke', '撤回') + ` — ${s.filename}?`)) return;
    setBusy(s.id);
    try {
      await revokeShare(s.id);
      showSuccess('SYS020003', { name: s.filename });   // {name}刪除成功（撤回）
      await load();
    } catch (err) { showApiError(err); }
    finally { setBusy(null); }
  };

  if (permLoading) return null;
  if (!canRead) {
    return (
      <div>
        <FunctionPageHeader funcCode={FUNC} />
        <div style={{ padding: 24, color: '#d32f2f' }}>{t('common.noPermission', '您沒有權限存取此功能')}</div>
      </div>
    );
  }

  // 收到的分享依分享者分組（=「分享來自 XX」虛擬資料夾）
  const grouped: Record<string, ShareInbox[]> = {};
  inbox.forEach((s) => {
    const k = s.from_username || `#${s.from_user_id}`;
    (grouped[k] = grouped[k] || []).push(s);
  });

  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', background: '#f5f5f5', borderBottom: '1px solid #ddd' };
  const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid #eee' };
  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '8px 18px', border: 'none', borderBottom: active ? '2px solid #1976d2' : '2px solid transparent',
    background: 'none', cursor: 'pointer', fontWeight: active ? 600 : 400, color: active ? '#1976d2' : '#555',
  });
  const btn = (color: string): React.CSSProperties => ({
    padding: '2px 10px', border: `1px solid ${color}`, color, background: 'none',
    borderRadius: 4, cursor: 'pointer', marginRight: 6,
  });

  // 收到列的操作：file→預覽/複製；folder→展開；org tab 另加「收進租戶硬碟」(role=3)
  const inboxActions = (s: ShareInbox, withCopyTenant: boolean) => (
    <>
      {s.resource_kind === 'folder' ? (
        <button style={btn('#1976d2')} onClick={() => doExpand(s)}>
          {expanded[s.id] ? t('common.close', '關閉') : t('driveShare.expandFolder', '展開資料夾')}
        </button>
      ) : (
        <button style={btn('#1976d2')} onClick={() => doPreview(s.id)}>
          {t('driveShare.previewFile', '預覽檔案')}
        </button>
      )}
      {s.resource_kind !== 'folder' && canCreate && (
        <button style={btn('#1976d2')} disabled={busy === s.id} onClick={() => doCopy(s)}>
          {t('driveShare.copyToMyDrive', '複製到我的硬碟')}
        </button>
      )}
      {withCopyTenant && canCopyToTenant && (
        <button style={btn('#2e7d32')} disabled={busy === s.id} onClick={() => doCopyToTenant(s)}>
          {t('driveShare.copyToTenantDrive', '收進租戶硬碟')}
        </button>
      )}
    </>
  );
  const childRows = (s: ShareInbox) => (expanded[s.id] || []).map((c) => (
    <tr key={`${s.id}-${c.child_id}`}>
      <td style={{ ...td, paddingLeft: 32 }}>↳ 📄 {c.filename}</td>
      <td style={td}>{fmtSize(c.size_bytes)}</td>
      <td style={td} />
      <td style={td}>
        <button style={btn('#1976d2')} onClick={() => doPreview(s.id, c.child_id)}>
          {t('driveShare.previewFile', '預覽檔案')}
        </button>
      </td>
    </tr>
  ));

  return (
    <div>
      <FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: '12px 24px' }}>
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e0e0e0', marginBottom: 16 }}>
          <button style={tabBtn(tab === 'inbox')} onClick={() => setTab('inbox')}>
            {t('driveShare.inbox', '收到的分享')}（{inbox.length}）
          </button>
          <button style={tabBtn(tab === 'mine')} onClick={() => setTab('mine')}>
            {t('driveShare.sharedByMe', '我分享的')}（{mine.length}）
          </button>
          <button style={tabBtn(tab === 'org')} onClick={() => setTab('org')}>
            {t('driveShare.tabOrgInbox', '組織收到')}（{orgInbox.length}）
          </button>
          <button style={{ ...tabBtn(false), marginLeft: 'auto' }} onClick={load}>
            {t('common.refresh', '重新整理')}
          </button>
        </div>

        {tab === 'inbox' && (
          Object.keys(grouped).length === 0
            ? <div style={{ color: '#999', padding: 24 }}>{t('driveShare.noShares', '（無分享）')}</div>
            : Object.entries(grouped).map(([sharer, items]) => (
              <div key={sharer} style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  📂 {t('driveShare.receivedFrom', { name: sharer, defaultValue: `分享來自：${sharer}` })}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead><tr>
                    <th style={th}>{t('myDrive.filename', '檔名')}</th>
                    <th style={th}>{t('myDrive.size', '大小')}</th>
                    <th style={th}>{t('driveShare.note', '分享說明')}</th>
                    <th style={th}>{t('common.actions', '操作')}</th>
                  </tr></thead>
                  <tbody>
                    {items.map((s) => (
                      <React.Fragment key={s.id}>
                        <tr>
                          <td style={td}>{s.resource_kind === 'folder' ? '📁' : '📄'} {s.filename}</td>
                          <td style={td}>{s.resource_kind === 'folder' ? '—' : fmtSize(s.size_bytes)}</td>
                          <td style={td}>{s.note || '—'}</td>
                          <td style={td}>{inboxActions(s, false)}</td>
                        </tr>
                        {childRows(s)}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
        )}

        {tab === 'org' && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr>
              <th style={th}>{t('myDrive.filename', '檔名')}</th>
              <th style={th}>{t('myDrive.size', '大小')}</th>
              <th style={th}>{t('driveShare.from', '來自')}</th>
              <th style={th}>{t('common.actions', '操作')}</th>
            </tr></thead>
            <tbody>
              {orgInbox.length === 0 && (
                <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#999', padding: 24 }}>
                  {t('driveShare.noShares', '（無分享）')}
                </td></tr>
              )}
              {orgInbox.map((s) => (
                <React.Fragment key={s.id}>
                  <tr>
                    <td style={td}>{s.resource_kind === 'folder' ? '📁' : '📄'} {s.filename}</td>
                    <td style={td}>{s.resource_kind === 'folder' ? '—' : fmtSize(s.size_bytes)}</td>
                    <td style={td}>{s.from_username || `#${s.from_user_id}`}</td>
                    <td style={td}>{inboxActions(s, true)}</td>
                  </tr>
                  {childRows(s)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'mine' && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr>
              <th style={th}>{t('myDrive.filename', '檔名')}</th>
              <th style={th}>{t('driveShare.to', '分享給')}</th>
              <th style={th}>{t('driveShare.note', '分享說明')}</th>
              <th style={th}>{t('common.status', '狀態')}</th>
              <th style={th}>{t('common.actions', '操作')}</th>
            </tr></thead>
            <tbody>
              {mine.length === 0 && (
                <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#999', padding: 24 }}>
                  {t('driveShare.noShares', '（無分享）')}
                </td></tr>
              )}
              {mine.map((s) => (
                <tr key={s.id}>
                  <td style={td}>{s.filename}</td>
                  <td style={td}>{s.to_username || `#${s.to_user_id}`}</td>
                  <td style={td}>{s.note || '—'}</td>
                  <td style={td}>{s.revoked_at ? t('driveShare.revoked', '已撤回') : '✅'}</td>
                  <td style={td}>
                    {!s.revoked_at && canDelete && (
                      <button onClick={() => doRevoke(s)} disabled={busy === s.id}
                        style={{ padding: '2px 10px', border: '1px solid #d32f2f', color: '#d32f2f', background: 'none', borderRadius: 4, cursor: 'pointer' }}>
                        {t('driveShare.revoke', '撤回')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SharedWithMePage;
